import * as Sentry from "@sentry/vue";
import { App } from "vue";
import { configuration } from "../Common/Helpers/configuration";

const prodTracingTargets = [/^https:\/\/(s1|s2)\.yapla\.com/];
const devTracingTargets = [
    /^https:\/\/((staging|staging2)\.s1|preprod\.s2)\.yapla\.com/,
    "http://main.yapla.local/",
];

export enum EntryPoint {
    Yapla = 'yapla',
    CMS = 'cms'
};

const cmsConfig = {
    sampleRate: 0.1,
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0.25,
    profilesSampleRate: 0.05,
} satisfies Sentry.BrowserOptions;

const yaplaConfig = {
    sampleRate: 0.5,
    tracesSampleRate: 0.5,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0.5,
    profilesSampleRate: 0.1,
} satisfies Sentry.BrowserOptions;

export const loadSentry = (entryPoint: EntryPoint) => {
    let release = import.meta.env.VITE_APP_VERSION as string | undefined;
    if (release !== undefined) {
        release = release.replace(/^v/, '');
    }
    Sentry.init({
        dsn: import.meta.env.VITE_SENTRY_DSN,
        environment: import.meta.env.VITE_ENVIRONMENT ?? 'development',
        release,
        integrations: [
            Sentry.browserTracingIntegration(),
            Sentry.replayIntegration({
                blockAllMedia: false
            }),
            Sentry.globalHandlersIntegration({
                onerror: false,
                onunhandledrejection: false
            }),
            Sentry.browserApiErrorsIntegration({
                eventTarget: false,
                requestAnimationFrame: false,
                setInterval: false,
                setTimeout: false,
                XMLHttpRequest: false
            })
        ],
        tracePropagationTargets: import.meta.env.VITE_ENVIRONMENT === 'production' ? prodTracingTargets : devTracingTargets,
        beforeSend: (event) => {
            event.tags = event.tags || {};
            event.tags['yapla.company_id'] = configuration.id;
            event.tags['yapla.entry_point'] = entryPoint;
            return event;
        },
        ...((entryPoint === EntryPoint.CMS) ? cmsConfig : yaplaConfig)
    });
    delayedAttachments.forEach(attach => attach());
};

const delayedAttachments: (() => void)[] = [];

export const attachSentryToApp = (app: App) => {
    app.mixin(
        Sentry.createTracingMixins({
            trackComponents: true,
            hooks: ['activate', 'mount', 'update'],
            timeout: 2000,
        })
    );
    const attach = () => {
        Sentry.attachErrorHandler(app, {
            attachErrorHandler: true,
            attachProps: true
        });
    }

    if (!Sentry.isInitialized()) {
        delayedAttachments.push(attach);
    } else {
        attach();
    }
};